<script setup lang="ts">
interface RadioOption {
  name: string;
  value: string | number | boolean;
  icon?: string;
}

interface Props {
  title: string;
  options: RadioOption[];
  value?: string | number | boolean | null;
  subtitle?: string;
  width?: string;
  errorMessage?: string;
  required?: boolean;
  showTitle?: boolean;
  custom?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  value: null,
  subtitle: "",
  width: "33%",
  errorMessage: "",
  required: false,
  showTitle: true,
  custom: false,
});

const emit = defineEmits<{
  returnValue: [value: string | number | boolean];
}>();

// Local copy so the selection (and the custom icon fill) updates instantly on
// click — mirrors old-site. The value is also emitted up to the parent form.
const localValue = ref<string | number | boolean | null>(props.value);
watch(
  () => props.value,
  (v) => {
    localValue.value = v;
  },
);

function selectOption(value: string | number | boolean) {
  localValue.value = value;
  emit("returnValue", value);
}

// For custom icon radios: 3+ options behave like a star rating — fill every icon
// up to the selected value (e.g. 3/5 fills the first three stars). 2 options
// (e.g. smile/frown) just highlight the selected one.
function isFilled(index: number): boolean {
  if (props.options.length > 2) return index < Number(localValue.value);
  return localValue.value === props.options[index]?.value;
}
</script>

<template>
  <div class="radio-input">
    <label
      v-if="showTitle && title"
      class="radio-input__title"
    >
      {{ title }}
    </label>
    <p
      v-if="subtitle"
      class="radio-input__subtitle"
    >
      {{ subtitle }}
    </p>

    <div class="radio-input__options">
      <label
        v-for="(option, i) in options"
        :key="option.value"
        :class="['radio-input__item', { selected: localValue === option.value }]"
        :style="custom ? undefined : { width }"
        @click="selectOption(option.value)"
      >
        <template v-if="custom">
          <fa
            :icon="['fas', option.icon ?? option.name]"
            :class="['radio-input__icon', { 'radio-input__icon--filled': isFilled(i) }]"
          />
        </template>
        <template v-else>
          <span :class="['radio-input__circle', { checked: localValue === option.value }]" />
          <span>{{ option.name }}</span>
        </template>
      </label>
    </div>
    <BannerError :message="errorMessage" />
  </div>
</template>

<style lang="scss">
.radio-input {
  display: flex;
  flex-direction: column;

  &__title {
    display: block;
    margin-bottom: 0.25rem;
    i {
      color: var(--color-pink-1);
    }
  }

  &__subtitle {
    margin: 0 0 0.5rem;
    padding: 0;
  }

  &__options {
    list-style-type: none;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    margin: 0;
    padding: 0;
    gap: 0.5rem;
  }

  &__item {
    display: flex;
    align-items: center;
    margin-right: 1rem;
    cursor: pointer;
    span {
      margin-left: 5px;
    }
    &.selected {
      .radio-input__circle {
        background-color: var(--color-pink-1);
        border-color: var(--color-pink-1);
      }
    }
  }

  &__icon {
    font-size: 2rem;
    color: var(--color-grey-3);
    cursor: pointer;
    &--filled {
      color: var(--color-pink-1);
    }
  }

  &__circle {
    display: inline-block;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 2px solid var(--color-grey-3);
    background-color: transparent;
    transition: background-color 0.2s;
    &.checked {
      background-color: var(--color-pink-1);
      border-color: var(--color-pink-1);
    }
  }
}
</style>
